import * as THREE from 'three';
import { NODE_CORE_RADIUS, layoutScaleOf } from '../createNeuralGraphLayer/constants';

const BOX_PAD = 8;
const BOX_HOLD_PAD = 10;
const IDLE_BOX_PAD = 6;
const IDLE_BOX_HOLD_PAD = 8;
const OVERLAP_EPS = 0.02;
/** World gap from the disk rim to the nearest edge of a quiet name. */
export const LABEL_GAP = 0.11;
/** Extra screen air for available / in-progress persistent names. */
export const LIVE_LABEL_PAD_PX = 8;

export function isLiveNamed(meta) {
  return !!(meta?.isAvailable || meta?.statusCategory === 'in_progress');
}

/** Live names sit a few pixels further out so they do not kiss the glow. */
export function liveLabelGap(mesh, camera, viewH) {
  if (!isLiveNamed(mesh?.userData?.nodeMeta)) return LABEL_GAP;
  let extra = 0.2;
  if (camera && mesh?.position) {
    const h = Math.max(viewH || 1, 1);
    const dist = camera.position.distanceTo(mesh.position);
    const tan = Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);
    extra = Math.max(0.16, ((2 * tan * dist) / h) * LIVE_LABEL_PAD_PX);
  }
  return LABEL_GAP + extra;
}
const PARENT_SEAT_COUNT = 16;
const PARENT_REACH = [1, 1.4, 1.85];
/** Hidden names stay down this many clear frames so neighbors cannot blink. */
export const YIELD_HIDE = 28;
/** After a confirmed walk, ignore grazes so the name cannot hop straight back. */
const SEAT_LOCK = 40;
/** A better seat must win this many frames in a row before we move. */
export const SEAT_CONFIRM = 8;
/** Stay put unless this much of the name is actually covered. */
const LEAVE_OVERLAP = 0.14;

const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _world = new THREE.Vector3();
const _ndc = new THREE.Vector3();
const _ndcRim = new THREE.Vector3();

export function setLabelCameraBasis(camera) {
  camera.getWorldDirection(_fwd);
  _right.crossVectors(_fwd, camera.up);
  if (_right.lengthSq() < 1e-8) return false;
  _right.normalize();
  _up.crossVectors(_right, _fwd).normalize();
  return true;
}

function labelSize(el) {
  const w = el?.offsetWidth;
  const h = el?.offsetHeight;
  if (w > 2 && h > 2) return { w, h };
  const chars = Math.min((el?.textContent || '').length, 28);
  return { w: Math.min(14 + chars * 6.1, 180), h: 18 };
}

export function boxOverlap(a, b) {
  if (!a || !b) return 0;
  const x = Math.max(0, Math.min(a.left + a.w, b.left + b.w) - Math.max(a.left, b.left));
  const y = Math.max(0, Math.min(a.top + a.h, b.top + b.h) - Math.max(a.top, b.top));
  if (x <= 0 || y <= 0) return 0;
  return (x * y) / Math.max(a.w * a.h, 1);
}

export function inflateBox(box, pad = BOX_PAD) {
  if (!box) return box;
  return { left: box.left - pad, top: box.top - pad, w: box.w + pad * 2, h: box.h + pad * 2 };
}

export function boxHitsAny(box, others, pad = BOX_PAD) {
  if (!box || !others) return false;
  for (let i = 0; i < others.length; i += 1) {
    if (boxOverlap(box, inflateBox(others[i], pad)) > OVERLAP_EPS) return true;
  }
  return false;
}

export function namePad(held = false, manual = true) {
  if (manual) return held ? BOX_HOLD_PAD : BOX_PAD;
  return held ? IDLE_BOX_HOLD_PAD : IDLE_BOX_PAD;
}

/** Hide if this name hits a placed name/title, or (nested only) a shallower disk. */
export function labelHitsOccupied(box, placed, disks, depth, tid, held = false, manual = true) {
  if (!box) return false;
  if (boxHitsAny(box, placed, namePad(held, manual))) return true;
  const diskExtra = !manual && held ? 3 : held ? 8 : 0;
  return depth > 0 && boxHitsShallowerDisk(box, disks, depth, tid, diskExtra);
}

/** Screen-space centre of the tag so it hangs away from the disk. 0 = down. */
export function labelCenterForAngle(angle) {
  return {
    x: 0.5 - Math.sin(angle) * 0.5,
    y: 0.5 - Math.cos(angle) * 0.5,
  };
}

export function fanAngles(start, count = PARENT_SEAT_COUNT) {
  const step = (Math.PI * 2) / count;
  const out = [start];
  for (let i = 1; i < count; i += 1) {
    const k = Math.ceil(i / 2);
    out.push(start + (i % 2 === 1 ? 1 : -1) * k * step);
  }
  return out;
}

export function boxHitsOtherDisk(box, disks, selfTid, extraR = 0) {
  if (!box || !disks) return false;
  for (let i = 0; i < disks.length; i += 1) {
    const disk = disks[i];
    if (disk.tid === selfTid) continue;
    const cx = THREE.MathUtils.clamp(disk.x, box.left, box.left + box.w);
    const cy = THREE.MathUtils.clamp(disk.y, box.top, box.top + box.h);
    const dx = cx - disk.x;
    const dy = cy - disk.y;
    const r = disk.r + extraR;
    if (dx * dx + dy * dy < r * r) return true;
  }
  return false;
}

export function parentSeatBlocked(box, placed, disks, tid, held = false, manual = true) {
  if (!box) return true;
  if (labelHitsOccupied(box, placed, disks, 0, tid, held, manual)) return true;
  const diskExtra = manual ? 6 : 4;
  return boxHitsOtherDisk(box, disks, tid, diskExtra);
}

/** Screen disks of visible nodes, for nested names that must yield to parents. */
export function collectNodeDiskScreens(meshByTaskId, camera, width, height) {
  const out = [];
  if (!meshByTaskId || !camera) return out;
  meshByTaskId.forEach((mesh, tid) => {
    const ud = mesh.userData;
    if (!mesh.visible || ud.exiting || (ud.visT ?? 1) < 0.02) return;
    const r = NODE_CORE_RADIUS * layoutScaleOf(mesh);
    _ndc.copy(mesh.position).project(camera);
    if (_ndc.z < -1 || _ndc.z > 1) return;
    _world.copy(mesh.position).addScaledVector(_right, r);
    _ndcRim.copy(_world).project(camera);
    const sx = (_ndc.x * 0.5 + 0.5) * width;
    const sy = (-_ndc.y * 0.5 + 0.5) * height;
    const rPx = Math.max(6, Math.hypot(
      (_ndcRim.x - _ndc.x) * 0.5 * width,
      (_ndcRim.y - _ndc.y) * 0.5 * height,
    ));
    out.push({
      tid,
      depth: ud.nodeMeta?.nestDepth ?? 0,
      x: sx,
      y: sy,
      r: rPx + 6,
    });
  });
  return out;
}

export function boxHitsShallowerDisk(box, disks, depth, selfTid, extraR = 0) {
  if (!box || !disks) return false;
  for (let i = 0; i < disks.length; i += 1) {
    const disk = disks[i];
    if (disk.tid === selfTid || disk.depth >= depth) continue;
    const cx = THREE.MathUtils.clamp(disk.x, box.left, box.left + box.w);
    const cy = THREE.MathUtils.clamp(disk.y, box.top, box.top + box.h);
    const dx = cx - disk.x;
    const dy = cy - disk.y;
    const r = disk.r + extraR;
    if (dx * dx + dy * dy < r * r) return true;
  }
  return false;
}

/** Screen boxes of mounted mission names, using the same projection as seat picking. */
export function collectVisibleLabelBoxes(labelByTaskId, camera, container, meshByTaskId) {
  const boxes = [];
  if (!labelByTaskId || !camera || !container) return boxes;
  const width = Math.max(container.clientWidth, 1);
  const height = Math.max(container.clientHeight, 1);
  labelByTaskId.forEach((rec, tid) => {
    if (!rec.label?.parent) return;
    const el = rec.element;
    if (parseFloat(el.style.opacity) < 0.08) return;
    _ndc.copy(rec.label.position).project(camera);
    if (_ndc.z < -1 || _ndc.z > 1) return;
    const { w, h } = labelSize(el);
    const cx = rec.label.center?.x ?? 0.5;
    const cy = rec.label.center?.y ?? 0;
    const sx = (_ndc.x * 0.5 + 0.5) * width;
    const sy = (-_ndc.y * 0.5 + 0.5) * height;
    boxes.push({
      left: sx - w * cx,
      top: sy - h * cy,
      w,
      h,
      clusterKey: meshByTaskId?.get(tid)?.userData?.nodeMeta?.clusterKey,
    });
  });
  return boxes;
}

function screenBoxForLabel(camera, label, el, width, height) {
  const { w, h } = labelSize(el);
  _ndc.copy(label.position).project(camera);
  const sx = (_ndc.x * 0.5 + 0.5) * width;
  const sy = (-_ndc.y * 0.5 + 0.5) * height;
  const cx = label.center?.x ?? 0.5;
  const cy = label.center?.y ?? 0;
  return { left: sx - w * cx, top: sy - h * cy, w, h, z: _ndc.z };
}

/** Seat on the ring around the disk. Angle 0 = under, then CCW in the camera plane. */
export function placeLabelAtAngle(camera, mesh, label, el, width, height, angle = 0, reachMul = 1) {
  const diskR = NODE_CORE_RADIUS * layoutScaleOf(mesh);
  const reach = (diskR + liveLabelGap(mesh, camera, height)) * Math.max(reachMul, 1);
  const sr = Math.sin(angle);
  const cu = Math.cos(angle);
  label.position.copy(mesh.position)
    .addScaledVector(_right, sr * reach)
    .addScaledVector(_up, -cu * reach);
  const c = labelCenterForAngle(angle);
  label.center.set(c.x, c.y);
  label.userData.wx = label.position.x - mesh.position.x;
  label.userData.wy = label.position.y - mesh.position.y;
  label.userData.wz = label.position.z - mesh.position.z;
  return screenBoxForLabel(camera, label, el, width, height);
}

/** Hang the name under the disk, centered. */
export function placeLabelUnderNode(camera, mesh, label, el, width, height) {
  return placeLabelAtAngle(camera, mesh, label, el, width, height, 0, 1);
}

/**
 * Stay unless the name is truly covered. Arrive-pad grazes must not evict —
 * that is the Schmitt trigger that stops two seats from swapping.
 */
export function parentSeatEvicted(box, placed, disks, tid, manual = true) {
  if (!box) return true;
  const pad = manual ? 2 : 0;
  const diskExtra = manual ? 1 : 0;
  let hit = 0;
  if (placed) {
    for (let i = 0; i < placed.length; i += 1) {
      hit = Math.max(hit, boxOverlap(box, inflateBox(placed[i], pad)));
    }
  }
  if (boxHitsOtherDisk(box, disks, tid, diskExtra)) hit = Math.max(hit, 1);
  return hit > LEAVE_OVERLAP;
}

/** A candidate must win `need` frames in a row. Changing seats resets the count. */
export function seatSwitchReady(rec, angle, reachMul, need = SEAT_CONFIRM) {
  const same = rec.wantAngle === angle && rec.wantReach === reachMul;
  if (!same) {
    rec.wantAngle = angle;
    rec.wantReach = reachMul;
    rec.wantHold = 1;
    return rec.wantHold >= need;
  }
  rec.wantHold = (rec.wantHold ?? 0) + 1;
  return rec.wantHold >= need;
}

export function clearSeatWant(rec) {
  rec.wantAngle = null;
  rec.wantReach = null;
  rec.wantHold = 0;
}

function isTopLevelNode(meta) {
  if ((meta?.nestDepth ?? 0) > 0) return false;
  return !meta?.parentTaskId;
}

/** Top-level available / in-progress keep a name. Nested and quiet disks are hover-only. */
export function wantsPersistentName(meta, forced = false) {
  if (forced) return true;
  if (!isTopLevelNode(meta)) return false;
  const cat = meta?.statusCategory;
  if (cat === 'completed' || cat === 'blocked') return false;
  if (meta?.isLocked) return false;
  if (cat === 'in_progress') return true;
  return !!meta?.isAvailable;
}

/**
 * Failsafe: a name that yielded stays hidden until `need` consecutive clear
 * frames. Stops two neighbors from taking turns every tick.
 */
export function latchYield(rec, blocked, need = YIELD_HIDE) {
  if (blocked) {
    rec.yielded = true;
    rec.yieldHold = need;
    return true;
  }
  if ((rec.yieldHold ?? 0) > 0) {
    rec.yieldHold -= 1;
    rec.yielded = true;
    return true;
  }
  rec.yielded = false;
  rec.yieldHold = 0;
  return false;
}

function settleParentSeat(camera, mesh, rec, el, width, height, angle, reachMul) {
  const box = placeLabelAtAngle(camera, mesh, rec.label, el, width, height, angle, reachMul);
  return { box, angle, reachMul };
}

const HOVER_NAME_COST_LABEL = 240;
const HOVER_NAME_COST_TITLE = 240;
const HOVER_NAME_COST_DISK = 12;

export function hoverNameSeatCandidates(nx, ny, lw, lh, cw, ch, gap = 16) {
  const dirs = [
    [1, 0], [-1, 0], [0, 1], [0, -1],
    [0.9, 0.8], [-0.9, 0.8], [0.9, -0.8], [-0.9, -0.8],
  ];
  const out = [];
  [gap, gap + 14].forEach((g) => {
    dirs.forEach(([sx, sy]) => {
      let left = sx > 0 ? nx + g : sx < 0 ? nx - g - lw : nx - lw * 0.5;
      let top = sy > 0 ? ny + g : sy < 0 ? ny - g - lh : ny - lh * 0.5;
      left = THREE.MathUtils.clamp(left, 8, Math.max(8, cw - lw - 8));
      top = THREE.MathUtils.clamp(top, 8, Math.max(8, ch - lh - 8));
      out.push({ left, top });
    });
  });
  return out;
}

export function hoverSeatCost(box, labels, titles, disks, selfTid) {
  if (!box) return Infinity;
  let cost = 0;
  (labels || []).forEach((other) => {
    const o = boxOverlap(box, inflateBox(other, 6));
    if (o > OVERLAP_EPS) cost += HOVER_NAME_COST_LABEL + o * 400;
  });
  (titles || []).forEach((other) => {
    const o = boxOverlap(box, inflateBox(other, 6));
    if (o > OVERLAP_EPS) cost += HOVER_NAME_COST_TITLE + o * 400;
  });
  (disks || []).forEach((disk) => {
    if (disk.tid === selfTid) return;
    const cx = THREE.MathUtils.clamp(disk.x, box.left, box.left + box.w);
    const cy = THREE.MathUtils.clamp(disk.y, box.top, box.top + box.h);
    const dx = cx - disk.x;
    const dy = cy - disk.y;
    const r = disk.r + 2;
    if (dx * dx + dy * dy < r * r) cost += HOVER_NAME_COST_DISK;
  });
  return cost;
}

/** Safest hover seat: miss names and titles first, disks are an allowed last resort. */
export function pickHoverNameSeat(nx, ny, lw, lh, cw, ch, labels, titles, disks, selfTid, sticky) {
  const boxOf = (p) => ({ left: p.left, top: p.top, w: lw, h: lh });
  if (sticky && hoverSeatCost(boxOf(sticky), labels, titles, disks, selfTid) < HOVER_NAME_COST_LABEL) {
    return sticky;
  }
  let best = { left: THREE.MathUtils.clamp(nx + 16, 8, Math.max(8, cw - lw - 8)), top: THREE.MathUtils.clamp(ny - lh * 0.5, 8, Math.max(8, ch - lh - 8)) };
  let bestCost = hoverSeatCost(boxOf(best), labels, titles, disks, selfTid);
  const seats = hoverNameSeatCandidates(nx, ny, lw, lh, cw, ch);
  for (let i = 0; i < seats.length; i += 1) {
    const cost = hoverSeatCost(boxOf(seats[i]), labels, titles, disks, selfTid);
    if (cost < bestCost - 4) {
      best = seats[i];
      bestCost = cost;
    }
  }
  return best;
}

/** Keep the last parent seat if it still clears; otherwise walk the ring, then stay put. */
export function pickParentLabelSeat(camera, mesh, rec, el, tid, width, height, placed, disks, manual = true) {
  const lastA = rec.seatAngle ?? 0;
  const lastR = rec.seatReach ?? 1;
  const lastBox = placeLabelAtAngle(camera, mesh, rec.label, el, width, height, lastA, lastR);
  const lock = rec.seatLock ?? 0;
  if (rec.seatAngle != null && lock > 0) {
    rec.seatLock = lock - 1;
    return { box: lastBox, angle: lastA, reachMul: lastR };
  }
  if (rec.seatAngle != null && !parentSeatEvicted(lastBox, placed, disks, tid, manual)) {
    clearSeatWant(rec);
    return { box: lastBox, angle: lastA, reachMul: lastR };
  }
  const firstSeat = rec.seatAngle == null;
  const angles = fanAngles(lastA);
  for (let r = 0; r < PARENT_REACH.length; r += 1) {
    const reach = PARENT_REACH[r];
    for (let i = 0; i < angles.length; i += 1) {
      if (!firstSeat && reach === lastR && angles[i] === lastA) continue;
      const box = placeLabelAtAngle(camera, mesh, rec.label, el, width, height, angles[i], reach);
      if (box && !parentSeatBlocked(box, placed, disks, tid, false, manual)) {
        if (firstSeat || seatSwitchReady(rec, angles[i], reach)) {
          clearSeatWant(rec);
          rec.seatLock = SEAT_LOCK;
          return { box, angle: angles[i], reachMul: reach };
        }
        return settleParentSeat(camera, mesh, rec, el, width, height, lastA, lastR);
      }
    }
  }
  clearSeatWant(rec);
  return settleParentSeat(camera, mesh, rec, el, width, height, lastA, lastR);
}
